import React, { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Activity, Key, ExternalLink, Zap, Loader2, Linkedin, Instagram, Building2, Save, Plus, Trash2, Package, ChevronRight, CreditCard } from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api from "@/lib/api";

export default function Settings() {
  const [health, setHealth] = useState(null);
  const [me, setMe] = useState(null);
  const [autopilot, setAutopilot] = useState(null);
  const [savingAutopilot, setSavingAutopilot] = useState(false);
  const [runningNow, setRunningNow] = useState(false);

  useEffect(() => {
    api.get("/health").then((r) => setHealth(r.data));
    api.get("/auth/me").then((r) => setMe(r.data));
    api.get("/autopilot/status").then((r) => setAutopilot(r.data));
  }, []);

  const toggleAutopilot = async (val) => {
    setSavingAutopilot(true);
    try {
      await api.patch("/autopilot/settings", { autopilot_social: val });
      const { data } = await api.get("/autopilot/status");
      setAutopilot(data);
      toast.success(val ? "Autopilote ACTIVÉ" : "Autopilote désactivé");
    } catch (e) {
      toast.error("Erreur sauvegarde");
    } finally {
      setSavingAutopilot(false);
    }
  };

  const runNow = async () => {
    setRunningNow(true);
    try {
      const { data } = await api.post("/autopilot/run-now");
      toast.success(`${data.published}/${data.attempted} posts publiés`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <ConsoleShell>
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-3">Paramètres</div>
        <h1 className="font-display text-4xl tracking-tighter font-semibold mb-10" data-testid="settings-title">Console &amp; intégrations</h1>

        {/* Company info — legal + billing */}
        <CompanyInfoCard />

        {/* Portfolio — GABRYX apps */}
        <PortfolioCard />

        {/* SaaS Integrations — multi-app financial management */}
        <SaasIntegrationsCard />

        {/* Stripe — payments status */}
        <StripeIntegrationCard />

        {/* Social Autopilot */}
        <div className="bg-gradient-to-br from-amber-500/[0.06] to-transparent border border-amber-500/20 p-6 mb-6" data-testid="autopilot-card">
          <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
            <div className="flex items-start gap-3">
              <Zap className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-1">Mode autopilote — Social</div>
                <h2 className="font-display text-2xl tracking-tight font-semibold">
                  CLARA publie seule sur tes réseaux
                </h2>
                <p className="text-sm text-zinc-400 mt-2 max-w-xl leading-relaxed">
                  Quand activé, les posts sociaux préparés à 06:30 sont automatiquement publiés à 09:00
                  sur les plateformes que tu as connectées. Tu n&apos;interviens plus.
                </p>
              </div>
            </div>
            <label className="inline-flex items-center cursor-pointer gap-3" data-testid="autopilot-toggle-label">
              <span className="text-xs text-zinc-400">
                {autopilot?.user_settings?.autopilot_social ? "Activé" : "Désactivé"}
              </span>
              <input
                type="checkbox"
                className="sr-only peer"
                checked={!!autopilot?.user_settings?.autopilot_social}
                disabled={savingAutopilot}
                onChange={(e) => toggleAutopilot(e.target.checked)}
                data-testid="autopilot-toggle"
              />
              <div className="relative w-11 h-6 bg-zinc-800 peer-checked:bg-amber-400 transition rounded-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-5 after:h-5 after:rounded-full after:transition peer-checked:after:translate-x-5" />
            </label>
          </div>

          {/* Channels status */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <ChannelCard
              icon={Linkedin}
              name="LinkedIn"
              provider={autopilot?.providers?.linkedin?.provider === "webhook" ? "via Webhook" : "Unipile"}
              status={autopilot?.providers?.linkedin}
            />
            <ChannelCard
              icon={Instagram}
              name="Instagram"
              provider={autopilot?.providers?.instagram?.provider === "webhook" ? "via Webhook" : "Meta Graph"}
              status={autopilot?.providers?.instagram}
            />
            <ChannelCard
              icon={Activity}
              name="TikTok"
              provider={autopilot?.providers?.tiktok?.provider === "webhook" ? "via Webhook" : "Manuel"}
              status={autopilot?.providers?.tiktok}
            />
          </div>

          {/* Webhook config (recommended path) */}
          <WebhookConfig
            autopilot={autopilot}
            onSaved={async () => {
              const { data } = await api.get("/autopilot/status");
              setAutopilot(data);
            }}
          />

          {/* Action */}
          {autopilot?.user_settings?.autopilot_social && (
            <button
              onClick={runNow}
              disabled={runningNow}
              data-testid="autopilot-run-now"
              className="inline-flex items-center gap-2 px-3.5 py-2 text-sm bg-amber-400 text-black hover:bg-amber-300 transition disabled:opacity-50 font-medium mt-4"
            >
              {runningNow ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              {runningNow ? "Publication…" : "Lancer maintenant"}
            </button>
          )}
        </div>

        {/* Account */}
        <div className="bg-[#141414] border border-white/10 p-6 mb-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Compte</div>
          <h2 className="font-display text-xl mb-4">Identifiant</h2>
          <div className="font-mono text-sm text-zinc-300" data-testid="settings-email">{me?.email || "—"}</div>
        </div>

        {/* Chantix link */}
        <div className="bg-[#141414] border border-white/10 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Intégration CHANTIX</div>
              <h2 className="font-display text-xl">Lien M2M</h2>
            </div>
            {health && (
              <div className={`text-sm font-mono flex items-center gap-2 ${health.chantix_link === "ok" ? "text-emerald-400" : "text-red-400"}`} data-testid="settings-link-status">
                {health.chantix_link === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                {health.chantix_link === "ok" ? "connecté" : "déconnecté"}
              </div>
            )}
          </div>

          <div className="space-y-3 text-sm">
            <Row label="URL CHANTIX" value="https://chantier-mobile-1.emergent.host" mono />
            <Row label="Clé API" value="cxk_live_OBRQtFHbtr5CVIJGz4evMzFIRfVprgtb" mono masked />
            <Row label="Scopes" value={(health?.scopes || []).join(", ") || "—"} mono />
          </div>

          <a
            href="https://chantier-mobile-1.emergent.host/admin/api-keys"
            target="_blank"
            rel="noreferrer"
            data-testid="settings-manage-keys"
            className="mt-6 inline-flex items-center gap-2 text-sm border border-white/15 px-4 py-2 hover:bg-white/5 transition"
          >
            <Key className="w-3.5 h-3.5" /> Gérer mes clés API dans CHANTIX
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Models */}
        <div className="bg-[#141414] border border-white/10 p-6">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Modèles IA · 10 agents</div>
          <h2 className="font-display text-xl mb-4">Affectations</h2>
          <ul className="space-y-2.5 text-sm">
            {[
              { n: "GABY", m: "openai / gpt-5.2", role: "Orchestratrice" },
              { n: "JÉRÔME", m: "anthropic / claude-sonnet-4-5", role: "Directeur commercial" },
              { n: "THIERRY", m: "anthropic / claude-sonnet-4-5", role: "Conducteur de travaux" },
              { n: "CASPER", m: "anthropic / claude-sonnet-4-5", role: "Coordonnateur CSPS" },
              { n: "CLARA", m: "anthropic / claude-sonnet-4-5", role: "Marketing & client" },
              { n: "ULRICH", m: "anthropic / claude-sonnet-4-5", role: "Facturation & compta" },
              { n: "RHÉA", m: "anthropic / claude-sonnet-4-5", role: "Ressources humaines" },
              { n: "OPÉRA", m: "anthropic / claude-sonnet-4-5", role: "Ordonnancement OPC" },
              { n: "VISIO", m: "openai / gpt-5.2", role: "Reporting & analytics" },
              { n: "VOXY", m: "openai / gpt-5.2", role: "Standard téléphonique" },
            ].map((row) => (
              <li key={row.n} className="flex items-center justify-between border-b border-white/5 pb-2.5 last:border-0 last:pb-0">
                <div>
                  <span className="font-display text-base">{row.n}</span>
                  <span className="text-zinc-500 text-xs ml-2">{row.role}</span>
                </div>
                <span className="font-mono text-zinc-400 text-xs">{row.m}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-zinc-500 mt-4 font-mono">Universal Emergent Key · facturation à l&apos;usage</p>
        </div>
      </div>
    </ConsoleShell>
  );
}

function Row({ label, value, mono, masked }) {
  const display = masked && value ? value.slice(0, 12) + "•••••••••••••••••" + value.slice(-4) : value;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 border-b border-white/5 pb-3 last:border-0">
      <span className="text-xs uppercase tracking-[0.15em] text-zinc-500">{label}</span>
      <span className={`${mono ? "font-mono" : ""} text-zinc-300 break-all`}>{display}</span>
    </div>
  );
}

function ChannelCard({ icon: Icon, name, provider, status, setupUrl }) {
  const enabled = !!status?.enabled;
  return (
    <div className={`border p-3 transition ${enabled ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${enabled ? "text-emerald-400" : "text-zinc-500"}`} />
        <div className="text-sm font-medium">{name}</div>
        {enabled ? (
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 ml-auto" />
        ) : (
          <XCircle className="w-3.5 h-3.5 text-zinc-600 ml-auto" />
        )}
      </div>
      <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-wider">{provider}</div>
      <div className="text-xs mt-2 text-zinc-400">
        {enabled ? "Prêt à publier" : "Non connecté"}
      </div>
      {!enabled && setupUrl && (
        <a
          href={setupUrl}
          target="_blank"
          rel="noreferrer"
          className="text-[11px] text-amber-300 hover:underline inline-flex items-center gap-1 mt-2"
        >
          Configurer <ExternalLink className="w-2.5 h-2.5" />
        </a>
      )}
    </div>
  );
}


function WebhookConfig({ autopilot, onSaved }) {
  const current = autopilot?.user_settings?.social_webhook_url || "";
  const [url, setUrl] = useState(current);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => { setUrl(current); }, [current]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/autopilot/settings", { social_webhook_url: url.trim() });
      toast.success(url.trim() ? "Webhook enregistré" : "Webhook supprimé");
      onSaved?.();
    } catch (e) {
      toast.error("Erreur sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/autopilot/test-webhook");
      setTestResult(data);
      if (data.ok) {
        toast.success("Webhook OK — ton automation a bien reçu le ping");
      } else {
        toast.error("Webhook n'a pas répondu correctement");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Erreur réseau";
      setTestResult({ ok: false, error: detail });
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  const hasUrl = !!current;
  return (
    <div className="border border-emerald-500/20 bg-emerald-500/[0.04] p-4 mt-4" data-testid="webhook-config">
      <div className="flex items-start gap-2 mb-3">
        <Zap className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-0.5">Méthode recommandée · gratuite</div>
          <div className="text-sm font-medium">Webhook Make.com / Zapier / n8n</div>
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
            Crée un scénario Make.com (gratuit, 1 000 ops/mois) qui reçoit un webhook, puis publie sur LinkedIn / Instagram / X / TikTok via les modules natifs. Colle l&apos;URL du webhook ci-dessous.
          </p>
        </div>
      </div>

      <div className="flex gap-2 items-stretch">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://hook.eu2.make.com/xxxxxxxxxxxxxxxxxxxx"
          data-testid="webhook-url-input"
          className="flex-1 bg-black border border-white/10 px-3 py-2 text-sm font-mono focus:border-emerald-500/40 outline-none"
        />
        <button
          onClick={save}
          disabled={saving || url.trim() === current}
          data-testid="webhook-save"
          className="px-3 py-2 text-xs bg-white text-black hover:bg-zinc-200 transition disabled:opacity-50"
        >
          {saving ? "…" : "Enregistrer"}
        </button>
        <button
          onClick={test}
          disabled={testing || !hasUrl || url.trim() !== current}
          data-testid="webhook-test"
          className="px-3 py-2 text-xs border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 transition disabled:opacity-50 inline-flex items-center gap-1.5"
          title={!hasUrl ? "Enregistre d'abord le webhook" : "Tester le webhook"}
        >
          {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : "Tester"}
        </button>
      </div>

      {testResult && (
        <div className={`mt-3 text-xs px-3 py-2 border ${testResult.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
          <div className="font-mono">
            {testResult.ok ? "✓ Webhook accessible" : `✗ ${testResult.error || testResult.reason || "Échec"}`}
          </div>
          {testResult.status && <div className="text-[10px] opacity-70 mt-1">HTTP {testResult.status}</div>}
        </div>
      )}

      <details className="mt-3">
        <summary className="text-[11px] text-emerald-300 cursor-pointer hover:underline">
          🪄 Guide : créer le webhook Make.com en 3 minutes (gratuit)
        </summary>
        <ol className="mt-2 ml-4 text-[11px] text-zinc-400 space-y-1.5 list-decimal leading-relaxed">
          <li>Va sur <a href="https://www.make.com/en/register" target="_blank" rel="noreferrer" className="text-emerald-300 hover:underline">make.com → Sign up</a> (gratuit, 1000 ops/mois).</li>
          <li>Crée un nouveau scénario → ajoute le module <b>Webhooks → Custom webhook</b>. Copie l&apos;URL générée.</li>
          <li>Ajoute après : <b>LinkedIn → Create a Text Post</b> (ou Instagram, ou Twitter…). Connecte ton compte LinkedIn.</li>
          <li>Dans le champ <code>Text</code> du module LinkedIn, mappe la variable <code>caption</code> du webhook. Idem pour <code>hashtags</code>.</li>
          <li>Active le scénario, colle l&apos;URL ici, clique <b>Enregistrer</b> puis <b>Tester</b>.</li>
        </ol>
        <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
          Champs envoyés dans le webhook : <code>platform</code>, <code>caption</code>, <code>hashtags</code>, <code>visual_hint</code>, <code>video_script</code>, <code>task_id</code>, <code>owner_email</code>.
        </p>
      </details>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Company info section
// ---------------------------------------------------------------------------

const COMPANY_FIELDS = [
  { key: "name", label: "Nom commercial", ph: "GABRYX", col: 6 },
  { key: "legal_name", label: "Dénomination légale", ph: "GABRYX — Joël Castellanos (EI)", col: 6 },
  { key: "activity", label: "Activité", ph: "Édition de logiciels SaaS…", col: 12 },
  { key: "address_line1", label: "Adresse", ph: "35 rue de la Paix", col: 6 },
  { key: "address_line2", label: "Complément", ph: "Bâtiment / étage / boîte", col: 6 },
  { key: "city_zip", label: "Code postal + Ville", ph: "86300 Chauvigny", col: 6 },
  { key: "siren", label: "SIREN", ph: "499 913 622", col: 6 },
  { key: "rcs", label: "RCS / n° de gestion", ph: "RCS Poitiers · 2026A00461", col: 6 },
  { key: "ape", label: "Code APE", ph: "62.01Z", col: 6 },
  { key: "email", label: "Email pro", ph: "gabryx@chantix.pro", col: 6 },
  { key: "website", label: "Site web", ph: "gabryxai.fr", col: 6 },
  { key: "vat_mention", label: "Mention TVA", ph: "TVA non applicable, art. 293 B du CGI", col: 12 },
  { key: "bank", label: "Banque", ph: "N26", col: 6 },
  { key: "iban", label: "IBAN", ph: "FR76 …", col: 6 },
  { key: "bic", label: "BIC", ph: "NTSBFRM1XXX", col: 6 },
  { key: "regime", label: "Régime fiscal", ph: "Entrepreneur individuel — micro-entreprise", col: 6 },
];

function CompanyInfoCard() {
  const [values, setValues] = useState({});
  const [effective, setEffective] = useState({});
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => {
      setValues(r.data.saved || {});
      setEffective(r.data.effective || {});
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/company", values);
      setValues(data.saved);
      setEffective(data.effective);
      toast.success("Infos entreprise sauvegardées — appliquées aux futures factures");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const summary = `${effective.name || "—"} · SIREN ${effective.siren || "—"} · ${effective.city_zip || "—"}`;

  return (
    <div className="border border-white/10 bg-white/[0.02] mb-6" data-testid="company-info-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between p-6 hover:bg-white/[0.03] transition"
        data-testid="company-info-toggle"
      >
        <div className="flex items-start gap-3 text-left">
          <Building2 className="w-5 h-5 text-emerald-400 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-400 mb-1">Documents officiels</div>
            <div className="font-display text-2xl font-semibold tracking-tight">Infos entreprise</div>
            <div className="text-xs text-zinc-500 mt-1">{summary}</div>
          </div>
        </div>
        <ChevronRight className={`w-5 h-5 text-zinc-400 transition ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="p-6 pt-0 border-t border-white/5">
          <p className="text-xs text-zinc-500 mb-4">
            Ces infos alimentent tes factures, CGV, et documents officiels générés par ULRICH et CASPER.
          </p>
          <div className="grid grid-cols-12 gap-3">
            {COMPANY_FIELDS.map((f) => (
              <div key={f.key} className={`col-span-12 md:col-span-${f.col}`}>
                <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">{f.label}</label>
                <input
                  type="text"
                  value={values[f.key] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={effective[f.key] || f.ph}
                  data-testid={`company-field-${f.key}`}
                  className="w-full bg-black/40 border border-white/10 text-sm text-white px-3 py-2 focus:outline-none focus:border-emerald-500/40"
                />
              </div>
            ))}
          </div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="company-save-btn"
            className="mt-5 inline-flex items-center gap-2 bg-emerald-500 text-black px-5 py-2.5 text-sm font-medium hover:bg-emerald-400 transition disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Sauvegarde…" : "Sauvegarder"}
          </button>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Portfolio section — GABRYX apps
// ---------------------------------------------------------------------------

const APP_STATUS = {
  dev: { label: "En dev", cls: "text-amber-400 bg-amber-500/10" },
  beta: { label: "Beta", cls: "text-blue-400 bg-blue-500/10" },
  live: { label: "Live", cls: "text-emerald-400 bg-emerald-500/10" },
  paused: { label: "En pause", cls: "text-zinc-400 bg-zinc-500/10" },
};

function PortfolioCard() {
  const [apps, setApps] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/portfolio/apps");
      setApps(data.apps || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Supprimer l'app "${a.name}" du portefeuille ?`)) return;
    try {
      await api.delete(`/portfolio/apps/${a.id}`);
      toast.success(`${a.name} retiré du portefeuille`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="border border-white/10 bg-white/[0.02] mb-6" data-testid="portfolio-card">
      <div className="p-6 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Package className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-1">Portefeuille</div>
            <div className="font-display text-2xl font-semibold tracking-tight">Applications GABRYX</div>
            <div className="text-xs text-zinc-500 mt-1">{apps.length} app{apps.length > 1 ? "s" : ""} sous ta gestion</div>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5 transition"
          data-testid="portfolio-add-btn"
        >
          <Plus className="w-3.5 h-3.5" />
          Nouvelle app
        </button>
      </div>
      <div className="border-t border-white/5">
        {loading ? (
          <div className="p-6 text-xs text-zinc-500">Chargement…</div>
        ) : (
          apps.map((a) => {
            const st = APP_STATUS[a.status] || APP_STATUS.dev;
            return (
              <div key={a.id} className="px-6 py-4 border-b border-white/5 last:border-0 flex items-center gap-4" data-testid={`app-row-${a.id}`}>
                <div className="w-8 h-8 bg-white/5 grid place-items-center text-xs font-bold text-white">
                  {a.name?.[0] || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{a.name}</span>
                    {a.is_primary && <span className="text-[9px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 uppercase tracking-wider">Principal</span>}
                    <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-wider ${st.cls}`}>{st.label}</span>
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{a.tagline || a.description || "—"}</div>
                  {a.url && (
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:underline inline-flex items-center gap-1 mt-1">
                      {a.url} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
                {!a.is_primary && (
                  <button
                    onClick={() => remove(a)}
                    className="text-zinc-500 hover:text-red-400 p-1"
                    data-testid={`app-delete-${a.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
      {showAdd && (
        <AddAppModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

function AddAppModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ id: "", name: "", tagline: "", url: "", api_url: "", status: "dev" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.id.trim() || !form.name.trim()) {
      toast.error("id et nom obligatoires");
      return;
    }
    setSaving(true);
    try {
      await api.post("/portfolio/apps", { ...form, id: form.id.trim().toLowerCase() });
      toast.success(`${form.name} ajouté au portefeuille`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-black border border-white/10 w-full max-w-lg p-6 space-y-4" data-testid="add-app-modal">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-1">Portefeuille</div>
          <div className="text-lg font-medium">Nouvelle application</div>
          <div className="text-xs text-zinc-500 mt-1">Ajoute une app que tu veux piloter depuis GABRYX</div>
        </div>
        <div className="space-y-3">
          <Field label="ID (slug court, ex: monapp)" value={form.id} onChange={(v) => setForm({ ...form, id: v })} testid="new-app-id" />
          <Field label="Nom commercial" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="new-app-name" />
          <Field label="Baseline (1 phrase)" value={form.tagline} onChange={(v) => setForm({ ...form, tagline: v })} testid="new-app-tagline" />
          <Field label="URL publique" value={form.url} onChange={(v) => setForm({ ...form, url: v })} placeholder="https://…" testid="new-app-url" />
          <Field label="API base URL (optionnel)" value={form.api_url} onChange={(v) => setForm({ ...form, api_url: v })} placeholder="https://api.monapp.fr" testid="new-app-api-url" />
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Statut</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              data-testid="new-app-status"
              className="w-full bg-black/40 border border-white/10 text-sm text-white px-3 py-2 focus:outline-none focus:border-blue-500/40"
            >
              <option value="dev">En dev</option>
              <option value="beta">Beta</option>
              <option value="live">Live</option>
              <option value="paused">En pause</option>
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-xs px-4 py-2 border border-white/15 hover:bg-white/5">Annuler</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="new-app-save"
            className="text-xs px-4 py-2 bg-blue-500 text-white hover:bg-blue-400 inline-flex items-center gap-2 disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-black/40 border border-white/10 text-sm text-white px-3 py-2 focus:outline-none focus:border-white/30"
      />
    </div>
  );
}




// ---------------------------------------------------------------------------
// SaaS Integrations — multi-app management (Chantix, Hygix, N others)
// ---------------------------------------------------------------------------

const COVERAGE_META = {
  full:          { label: "Complète",       cls: "text-emerald-400 bg-emerald-500/10", hint: "Utilisateurs + activité + finance" },
  finance_only:  { label: "Finance seule",  cls: "text-amber-400 bg-amber-500/10",    hint: "Uniquement achats & abonnements" },
};

const TEST_STATUS = {
  ok:           { label: "Connecté",   cls: "text-emerald-400 bg-emerald-500/10", Icon: CheckCircle2 },
  auth_error:   { label: "Clé invalide", cls: "text-red-400 bg-red-500/10",       Icon: XCircle },
  unreachable:  { label: "Injoignable", cls: "text-red-400 bg-red-500/10",        Icon: XCircle },
};

function SaasIntegrationsCard() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [testing, setTesting] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/settings/saas-apps");
      setApps(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const testApp = async (a) => {
    setTesting((t) => ({ ...t, [a.id]: true }));
    try {
      const { data } = await api.post(`/settings/saas-apps/${a.id}/test`);
      const meta = TEST_STATUS[data.status];
      toast[data.status === "ok" ? "success" : "error"](
        data.status === "ok" ? `${a.name} : connecté ✓` : `${a.name} : ${data.result?.error || meta?.label}`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur test");
    } finally {
      setTesting((t) => ({ ...t, [a.id]: false }));
    }
  };

  const remove = async (a) => {
    if (a.is_primary) { toast.error("Impossible de supprimer l'app principale"); return; }
    if (!window.confirm(`Retirer « ${a.name} » de la gestion ?`)) return;
    try {
      await api.delete(`/settings/saas-apps/${a.id}`);
      toast.success(`${a.name} retiré`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="border border-white/10 bg-white/[0.02] mb-6" data-testid="saas-integrations-card">
      <div className="p-6 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-1">Intégrations SaaS</div>
            <div className="font-display text-2xl font-semibold tracking-tight">Apps sous gestion GABRYX</div>
            <div className="text-xs text-zinc-500 mt-1">
              Ajoute une app pour tracker ses abonnements et paiements ici. Mode « finance seule » = pas besoin de clé M2M.
            </div>
          </div>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-1.5 text-xs hover:bg-white/5 transition"
          data-testid="add-saas-app-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Ajouter une app
        </button>
      </div>

      <div className="border-t border-white/5">
        {loading ? (
          <div className="p-6 text-xs text-zinc-500">Chargement…</div>
        ) : apps.length === 0 ? (
          <div className="p-6 text-xs text-zinc-500">Aucune app configurée.</div>
        ) : (
          apps.map((a) => {
            const cov = COVERAGE_META[a.coverage_mode || "full"] || COVERAGE_META.full;
            const test = TEST_STATUS[a.last_test_status] || null;
            const TestIcon = test?.Icon;
            return (
              <div key={a.id} className="px-6 py-4 border-b border-white/5 last:border-0 flex items-center gap-4" data-testid={`saas-app-row-${a.code}`}>
                <div
                  className="w-9 h-9 grid place-items-center text-xs font-bold text-white"
                  style={{ background: a.is_primary ? "#3B82F6" : "rgba(255,255,255,0.05)" }}
                >
                  {a.name?.[0] || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{a.name}</span>
                    {a.is_primary && <span className="text-[9px] bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 uppercase tracking-wider">Principal</span>}
                    <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-wider ${cov.cls}`} title={cov.hint}>{cov.label}</span>
                    {test && (
                      <span className={`text-[9px] px-1.5 py-0.5 uppercase tracking-wider inline-flex items-center gap-1 ${test.cls}`}>
                        {TestIcon && <TestIcon className="w-2.5 h-2.5" />}
                        {test.label}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500 truncate">{a.description || "—"}</div>
                  {a.base_url && (
                    <a
                      href={a.base_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-blue-400 hover:underline inline-flex items-center gap-1 mt-1"
                    >
                      {a.base_url} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.base_url && (
                    <button
                      onClick={() => testApp(a)}
                      disabled={testing[a.id]}
                      className="text-[10px] px-2.5 py-1.5 border border-white/15 hover:bg-white/5 transition inline-flex items-center gap-1 disabled:opacity-40"
                      data-testid={`test-app-${a.code}`}
                    >
                      {testing[a.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Activity className="w-3 h-3" />}
                      Tester
                    </button>
                  )}
                  {!a.is_primary && (
                    <button
                      onClick={() => remove(a)}
                      className="text-zinc-500 hover:text-red-400 p-1"
                      data-testid={`delete-app-${a.code}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {showAdd && (
        <AddSaasAppModal
          onClose={() => setShowAdd(false)}
          onCreated={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}

function AddSaasAppModal({ onClose, onCreated }) {
  const [f, setF] = useState({
    name: "", description: "", coverage_mode: "finance_only",
    base_url: "", api_key: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Nom de l'app requis"); return; }
    if (f.coverage_mode === "full" && (!f.base_url.trim() || !f.api_key.trim())) {
      toast.error("Mode complet : URL + clé API requises");
      return;
    }
    setSaving(true);
    try {
      await api.post("/settings/saas-apps", f);
      toast.success(`${f.name} ajoutée à la gestion`);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-black border border-white/10 w-full max-w-lg p-6 my-8 space-y-4" data-testid="add-saas-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-1">Nouvelle intégration</div>
            <div className="text-lg font-medium">Ajouter une app à la gestion</div>
          </div>
          <button onClick={onClose}><XCircle className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
        </div>

        <Field label="Nom de l'app *" value={f.name} onChange={(v) => set("name", v)} placeholder="ex : HYGIX" testid="new-app-name" />
        <Field label="Description" value={f.description} onChange={(v) => set("description", v)} placeholder="ex : App santé & hygiène" testid="new-app-desc" />

        <div>
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Mode de gestion</label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { code: "finance_only", label: "Finance seule", hint: "Achats + abonnements. Aucune clé requise." },
              { code: "full",         label: "Complète",      hint: "Users + activité via M2M. Clé requise." },
            ].map((opt) => (
              <button
                key={opt.code}
                onClick={() => set("coverage_mode", opt.code)}
                data-testid={`coverage-${opt.code}`}
                className={`text-left p-3 border transition ${f.coverage_mode === opt.code ? "border-blue-500/60 bg-blue-500/5" : "border-white/10 hover:border-white/30"}`}
              >
                <div className={`text-sm font-medium mb-1 ${f.coverage_mode === opt.code ? "text-white" : "text-zinc-300"}`}>{opt.label}</div>
                <div className="text-[10px] text-zinc-500 leading-tight">{opt.hint}</div>
              </button>
            ))}
          </div>
        </div>

        {f.coverage_mode === "full" && (
          <>
            <Field label="URL de base *" value={f.base_url} onChange={(v) => set("base_url", v)} placeholder="https://mon-app.com" testid="new-app-url" />
            <Field label="Clé API (M2M) *" value={f.api_key} onChange={(v) => set("api_key", v)} placeholder="sk-…" testid="new-app-key" />
          </>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-xs px-4 py-2 border border-white/15 hover:bg-white/5">Annuler</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-new-app"
            className="text-xs px-4 py-2 bg-white text-black inline-flex items-center gap-2 hover:bg-zinc-200 disabled:opacity-40"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Ajouter
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stripe payments integration — live status + webhook audit
// ---------------------------------------------------------------------------

function StripeIntegrationCard() {
  const [state, setState] = useState({ loading: true, health: null });

  const load = () => {
    setState((s) => ({ ...s, loading: true }));
    api.get("/stripe/webhook-health")
      .then((r) => setState({ loading: false, health: r.data }))
      .catch(() => setState({ loading: false, health: null }));
  };
  useEffect(() => { load(); }, []);

  const { health, loading } = state;
  const secretOk = health?.secret_configured;
  const recent = health?.recent_events || [];

  return (
    <div className="border border-white/10 bg-white/[0.02] mb-6" data-testid="stripe-integration-card">
      <div className="p-6 flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <CreditCard className="w-5 h-5 text-violet-400 mt-0.5" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-violet-400 mb-1">
              Paiements · Stripe
            </div>
            <div className="font-display text-2xl font-semibold tracking-tight">
              Encaissement automatique
            </div>
            <div className="text-xs text-zinc-500 mt-1 max-w-xl">
              Quand un client paie via Stripe, JÉRÔME crée l&apos;abo, ULRICH génère la facture,
              RHÉA envoie le mail de bienvenue. Auto-routing entre CHANTIX et HYGIX.
            </div>
          </div>
        </div>
        <a
          href="https://dashboard.stripe.com/dashboard"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
        >
          Dashboard Stripe <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <div className="border-t border-white/5 grid grid-cols-1 sm:grid-cols-3">
        <StripeStatCell
          label="Mode"
          value={(process.env.REACT_APP_STRIPE_MODE || "live").toUpperCase()}
          hint="Test ou Live"
          ok={true}
        />
        <StripeStatCell
          label="Signing secret"
          value={secretOk ? "Configuré" : "Manquant"}
          hint="Vérifie l'authenticité des webhooks"
          ok={secretOk}
        />
        <StripeStatCell
          label="Webhook activé"
          value={loading ? "…" : (recent.length > 0 ? `${recent.length} events reçus` : "En attente")}
          hint="/api/stripe/webhook"
          ok={secretOk}
        />
      </div>

      {/* Recent events */}
      <div className="border-t border-white/5 p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">
          Derniers événements
        </div>
        {loading ? (
          <div className="text-xs text-zinc-500">Chargement…</div>
        ) : recent.length === 0 ? (
          <div className="text-xs text-zinc-500">
            Aucun paiement reçu pour l&apos;instant. Dès qu&apos;un client passe à la caisse, tu le verras ici.
          </div>
        ) : (
          <div className="space-y-1.5">
            {recent.slice(0, 5).map((e, i) => (
              <div
                key={i}
                className="flex items-center gap-3 text-xs py-1.5 border-b border-white/5 last:border-0"
                data-testid={`stripe-event-${i}`}
              >
                <span className="text-zinc-500 font-mono text-[10px] w-32 shrink-0">
                  {(e.at || "").slice(0, 16).replace("T", " ")}
                </span>
                <span className="text-zinc-300 font-mono text-[10px] w-44 shrink-0 truncate">
                  {e.type}
                </span>
                {e.app_code && (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-500/10 text-blue-300">
                    {e.app_code}
                  </span>
                )}
                {e.amount != null && (
                  <span className="text-zinc-300 font-mono ml-auto">
                    {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(e.amount)}
                  </span>
                )}
                {e.error && (
                  <span className="text-red-400 text-[10px]">✗ {e.error.slice(0, 40)}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-white/5 px-6 py-3 flex items-center justify-between text-[11px] text-zinc-500 flex-wrap gap-2">
        <div>
          URL webhook à copier dans Stripe : <span className="font-mono text-zinc-300">https://gabryxai.fr/api/stripe/webhook</span>
        </div>
        <a
          href="https://dashboard.stripe.com/webhooks"
          target="_blank"
          rel="noopener noreferrer"
          className="text-violet-400 hover:underline inline-flex items-center gap-1"
        >
          Gérer les webhooks <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

function StripeStatCell({ label, value, hint, ok }) {
  return (
    <div className="p-4 border-r border-white/5 last:border-r-0">
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className="text-lg font-medium text-white flex items-center gap-2">
        {ok
          ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          : <XCircle className="w-3.5 h-3.5 text-red-400" />}
        {value}
      </div>
      <div className="text-[10px] text-zinc-600 mt-0.5">{hint}</div>
    </div>
  );
}

